import { useId } from "react";
import { Checkbox, FormControlLabel, Stack, Typography as MuiTypography, useTheme } from "@mui/material";
import { FoodsCheckbox, FoodsTextInput, Typography as FoodsTypography } from "@foodpilot/foods";

import { JsonAttributeType, type JsonAttribute } from "src/api/client-api/jsonAttributes/useJsonAttribute";
import type { ComponentAppearance } from "src/components/appearance";

export type JsonAttributeFormValue = string | number | boolean | null;
type JsonAttributeLabelVariant = "body-semibold" | "h5";

type JsonAttributeFieldProps = {
    attribute: JsonAttribute;
    value: JsonAttributeFormValue | undefined;
    onChange: (value: JsonAttributeFormValue) => void;
    labelVariant?: JsonAttributeLabelVariant;
    labelSuffix?: "" | ":";
    appearance?: ComponentAppearance;
};

const normalizeAttributeLabel = (label: string): string => {
    const normalizedInitialism = label.replace(/\bid\b/gi, "ID");

    return normalizedInitialism.replace(/^./u, (firstCharacter) => firstCharacter.toLocaleUpperCase());
};

/**
 * Shared JSON-attribute leaf field.
 *
 * Entity wrappers own titles, collections and state updates. This component
 * owns only the type-specific control and the existing value coercion rules.
 */
export const JsonAttributeField = (props: JsonAttributeFieldProps) => {
    const {
        attribute,
        value,
        onChange,
        labelVariant = "body-semibold",
        labelSuffix = "",
        appearance = "legacy",
    } = props;
    const theme = useTheme();
    const isProductUxV2 = appearance === "product-v2";
    const inputId = useId();
    const fieldLabel = isProductUxV2 ? normalizeAttributeLabel(attribute.label) : attribute.label;
    const label = `${fieldLabel}${labelSuffix}`;

    if (attribute.type === JsonAttributeType.BOOLEAN) {
        if (!isProductUxV2) {
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value === true}
                            onChange={(event) => onChange(event.currentTarget.checked)}
                        />
                    }
                    label={<MuiTypography variant={labelVariant}>{label}</MuiTypography>}
                />
            );
        }

        return (
            <Stack gap={theme.spacing(1)} minWidth={0}>
                <FoodsCheckbox
                    values={[attribute]}
                    selectedItems={value === true ? [attribute] : []}
                    onChange={(selectedItems) => onChange(selectedItems.length > 0)}
                    getId={(item) => item.identifier}
                    getName={() => label}
                    FormControlLabelProps={{
                        sx: {
                            gap: theme.spacing(2),
                            margin: theme.spacing(0),
                            "& .MuiFormControlLabel-label": theme.typography[labelVariant],
                        },
                    }}
                />
            </Stack>
        );
    }

    const isNumeric = attribute.type === JsonAttributeType.INTEGER || attribute.type === JsonAttributeType.FLOAT;
    const inputValue = value === null || value === undefined ? "" : String(value);

    const handleTextChange = (nextValue: string) => {
        if (!isNumeric) {
            onChange(nextValue);
            return;
        }

        if (nextValue.trim() === "") {
            onChange(null);
            return;
        }

        const numericValue = Number.parseFloat(nextValue);
        onChange(Number.isNaN(numericValue) ? nextValue : numericValue);
    };

    return (
        <Stack gap={theme.spacing(1)} minWidth={0}>
            {isProductUxV2 ?
                <FoodsTypography component="label" htmlFor={inputId} variant={labelVariant}>
                    {label}
                </FoodsTypography>
            :   <MuiTypography component="label" htmlFor={inputId} variant={labelVariant}>
                    {label}
                </MuiTypography>
            }
            <FoodsTextInput
                value={inputValue}
                onChange={handleTextChange}
                ExtraInputProps={{
                    id: inputId,
                    ...(isNumeric ? { type: "number" } : {}),
                    ...(isProductUxV2 ?
                        {
                            sx: {
                                "&.MuiInputBase-root": {
                                    borderColor: theme.custom.strokes.containerDivider.color,
                                    borderStyle: theme.custom.strokes.containerDivider.style,
                                    borderWidth: theme.custom.strokes.containerDivider.width,
                                    paddingInline: theme.spacing(2),
                                    ...theme.typography["body-regular"],
                                },
                            },
                        }
                    :   {}),
                }}
            />
        </Stack>
    );
};
